import {
  IDENTITY_ERROR_CODES,
  createIdentityApiError,
  createMagicLinkError,
  magicLinkErrorFromCode,
  magicLinkErrorFromConsumeResponse,
} from '@identity/application/errors';
import type { CreatePasswordDto, SendVerificationLinkResult } from '@identity/application/dtos';
import type { GetAccountStatusOptions, IAuthApiPort } from '@identity/application/ports';
import { LINK_PURPOSES } from '@identity/domain/types';
import type { LinkPurpose, RequestAuthorizer, VerificationChannel } from '@identity/domain/types';
import type { AccountStatus } from '@identity/domain/entities';
import type { AuthApiErrorBody, AuthApiValidationErrors } from '@identity/infrastructure/api/types';
import type { AuthHttpClientType } from '@identity/infrastructure/api/AuthHttpClient';
import { mapApiErrorCode, mapApiStatusToAccountStatus, mapValidationErrorCode } from '@identity/infrastructure/mappers';
import type { ValidationField } from '@identity/infrastructure/mappers';

/** Shape of an axios error, read carefully: a network failure has no `response` at all. */
type HttpFailure = { response?: { status?: number; data?: AuthApiErrorBody } };

/** Everything the repository needs from a failed request. `status` is `0` when there was no response. */
type FailureFields = {
  status: number;
  apiCode?: string;
  retryAfterSeconds?: number;
  validation?: AuthApiValidationErrors | null;
};

/** Reads the status and the first entry of the error envelope, assuming both may be missing. */
const readFailure = (error: unknown): FailureFields => {
  const { response } = (error ?? {}) as HttpFailure;
  const [first] = response?.data?.errors ?? [];

  return {
    status: response?.status ?? 0,
    apiCode: first?.code,
    retryAfterSeconds: first?.retryAfterSeconds,
    validation: response?.data?.validation,
  };
};

/** What only the caller of a request knows, and what two of the translations depend on. */
type FailureContext = {
  /** True only for the consume, the one endpoint whose status alone can name a link code. */
  isConsume?: boolean;
  /** The field this call sent, when a serializer rejection has to be resolved by field. */
  validationField?: ValidationField;
  /** The channel of a link send, which is what decides the message of the generic `value` field. */
  channel?: VerificationChannel;
  /** True when the request named one verification, which is what gives `auth.LinkInvalid` its other reading. */
  isVerificationAnchored?: boolean;
};

/**
 * Converts an axios error into the error the UI understands, and throws it. This is the last place
 * that knows about the transport: nothing above it ever sees a `response`.
 */
const rethrowIdentityFailure = (error: unknown, authorizer: RequestAuthorizer, context: FailureContext = {}): never => {
  const failure = readFailure(error);

  const code =
    mapApiErrorCode(failure.apiCode, authorizer, { isVerificationAnchored: context.isVerificationAnchored }) ??
    (context.validationField
      ? mapValidationErrorCode({
          validation: failure.validation,
          field: context.validationField,
          channel: context.channel,
        })
      : undefined);

  const linkError = context.isConsume
    ? magicLinkErrorFromConsumeResponse(failure.status, code)
    : magicLinkErrorFromCode(code);

  // A network error or a 5xx is not a broken link. It keeps its own error so the screen can show
  // its own message.
  throw (
    linkError ??
    createIdentityApiError({
      message: error instanceof Error ? error.message : `Identity request failed with status ${failure.status}`,
      code,
      ...failure,
    })
  );
};

/**
 * Creates the auth repository. Besides calling the client and the mapper, its job is to keep HTTP
 * out of the upper layers: the UI never sees a status code, only a domain error code.
 *
 * It is built with the authorizer of its slice because a missing credential means one thing to a
 * wizard running on a link and another to one running on the Parrot session, and the response says
 * the same word in both cases.
 */
export const createAuthApiRepository = (props: {
  httpClient: AuthHttpClientType;
  authorizer: RequestAuthorizer;
}): IAuthApiPort => {
  const { httpClient, authorizer } = props;

  const rethrow = (error: unknown, context?: FailureContext): never =>
    rethrowIdentityFailure(error, authorizer, context);

  const consumeMagicLink = async (
    magicLinkToken: string,
  ): Promise<{ purpose: LinkPurpose; userId: string; linkToken: string }> => {
    let purpose: string;
    let userUuid: string;
    let accessToken: string;

    try {
      ({ purpose, userUuid, accessToken } = await httpClient.consumeMagicLink(magicLinkToken));
    } catch (error) {
      return rethrow(error, { isConsume: true });
    }

    if (!(LINK_PURPOSES as readonly string[]).includes(purpose)) {
      throw createMagicLinkError(IDENTITY_ERROR_CODES.LINK_INVALID);
    }

    return { purpose: purpose as LinkPurpose, userId: userUuid, linkToken: accessToken };
  };

  /**
   * This is the last place that knows whether the query named a verification, so it is where the
   * ambiguous rejection of a replaced one is told apart from a broken link.
   */
  const getAccountStatus = async (options?: GetAccountStatusOptions): Promise<AccountStatus> => {
    try {
      return mapApiStatusToAccountStatus(await httpClient.getStatus(options));
    } catch (error) {
      return rethrow(error, { isVerificationAnchored: Boolean(options?.verificationUuid) });
    }
  };

  const createPassword = async (input: CreatePasswordDto): Promise<void> => {
    try {
      await httpClient.createPassword(input);
    } catch (error) {
      rethrow(error, { validationField: 'password' });
    }
  };

  const requestPasswordRecovery = async (email: string): Promise<void> => {
    try {
      await httpClient.requestPasswordRecovery(email);
    } catch (error) {
      rethrow(error, { validationField: 'email' });
    }
  };

  const sendVerificationLink = async (input: {
    value: string;
    channel: VerificationChannel;
  }): Promise<SendVerificationLinkResult> => {
    try {
      const { verificationUuid } = await httpClient.sendLink(input);

      return { verificationUuid };
    } catch (error) {
      return rethrow(error, { validationField: 'value', channel: input.channel });
    }
  };

  return {
    consumeMagicLink,
    getAccountStatus,
    createPassword,
    requestPasswordRecovery,
    sendVerificationLink,
  };
};
